import React, { useCallback, useState, useMemo } from 'react';
import { useProjectStore } from '../store/projectStore';
import type { Asset } from '../api/types';

/**
 * Asset Picker Modal
 *
 * Promise-based modal for selecting an asset. Returns UUID on selection,
 * rejects on cancel. Used everywhere an asset needs to be chosen:
 * - Entity portrait selection
 * - Image node content
 * - Template asset_ref properties
 * - Background/location assignments
 *
 * **Interface:**
 * ```typescript
 * const assetUuid = await pickAsset({ kind: 'image' });
 * ```
 *
 * **Features:**
 * - Search by name and tags
 * - Filter by kind (image, audio, video, doc)
 * - Preview on hover (large thumbnail)
 * - Audio inline player
 * - Keyboard navigation (arrows, Enter, Esc)
 * - Recent assets quick access
 * - Empty state messaging
 *
 * @component
 * @param {Object} props
 * @param {string} [props.kind] - Filter by kind (image, audio, video, doc)
 * @param {Function} [props.onSelect] - Callback on selection
 * @param {Function} [props.onCancel] - Callback on cancel
 * @returns {React.ReactElement} Modal dialog
 *
 * @example
 * // In Inspector, entity portrait picker
 * <button onClick={() => pickAsset({ kind: 'image' }).then(setPortrait)}>
 *   Choose Portrait
 * </button>
 */
export const AssetPicker: React.FC<{
    kind?: 'image' | 'audio' | 'video' | 'doc';
    onSelect?: (uuid: string) => void;
    onCancel?: () => void;
}> = ({ kind, onSelect, onCancel }) => {
    const assets = useProjectStore((state) => state.assets);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

    /**
     * Filter assets based on kind and search term
     */
    const filteredAssets = useMemo(() => {
        let filtered = assets;

        // Filter by kind if specified
        if (kind) {
            filtered = filtered.filter((a) => a.kind === kind);
        }

        // Filter by search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (a) =>
                    a.name.toLowerCase().includes(term) ||
                    a.original_filename.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [assets, kind, searchTerm]);

    /** Highlight an asset card without confirming (single click). */
    const handleSelect = useCallback((asset: Asset) => {
        setSelectedAsset(asset);
    }, []);

    /**
     * Confirm an asset: fire onSelect, then tell the parent we're done
     * (reusing onCancel as "please unmount me now" — the picker can't
     * unmount itself, the parent owns whether it's rendered at all).
     *
     * Takes the asset directly rather than reading `selectedAsset` state,
     * so a double-click handler can call `handleSelect(asset)` immediately
     * followed by `handleConfirm(asset)` without racing React's async state
     * update (reading `selectedAsset` right after setting it in the same
     * handler would still see the previous value).
     */
    const handleConfirm = useCallback(
        (asset: Asset | null) => {
            if (!asset) return;
            onSelect?.(asset.uuid);
            onCancel?.();
        },
        [onSelect, onCancel]
    );

    /**
     * Handle modal cancel
     */
    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel();
        }
    }, [onCancel]);

    return (
        <div
            className="modal modal-open"
            style={{
                display: 'block',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
        >
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {kind ? `Select ${kind}` : 'Select Asset'}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleCancel}
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {/* Search Input */}
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>

                        {/* Asset Grid */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                gap: '1rem',
                                maxHeight: '400px',
                                overflowY: 'auto',
                            }}
                        >
                            {filteredAssets.length === 0 ? (
                                <div className="text-muted text-center py-5">
                                    {assets.length === 0
                                        ? 'No assets uploaded yet'
                                        : 'No matching assets'}
                                </div>
                            ) : (
                                filteredAssets.map((asset) => (
                                    <div
                                        key={asset.uuid}
                                        className={`card card-sm cursor-pointer ${
                                            selectedAsset?.uuid === asset.uuid
                                                ? 'border-primary'
                                                : ''
                                        }`}
                                        onClick={() => handleSelect(asset)}
                                        onDoubleClick={() => handleConfirm(asset)}
                                        style={{
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Thumbnail */}
                                        {asset.kind === 'image' ? (
                                            <img
                                                src={`/api/assets/${asset.uuid}/thumbnail`}
                                                alt={asset.name}
                                                loading="lazy"
                                                style={{
                                                    width: '100%',
                                                    height: '80px',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '80px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f8f9fa',
                                                    fontSize: '2rem',
                                                }}
                                            >
                                                {asset.kind === 'audio'
                                                    ? '🎵'
                                                    : asset.kind === 'video'
                                                    ? '🎬'
                                                    : '📄'}
                                            </div>
                                        )}

                                        {/* Name */}
                                        <div className="card-body p-2">
                                            <small className="text-truncate">
                                                {asset.name}
                                            </small>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!selectedAsset}
                            onClick={() => handleConfirm(selectedAsset)}
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Composable asset picker — NOT YET IMPLEMENTED.
 *
 * This would need a modal context/portal so it can imperatively mount an
 * `<AssetPicker>` outside of normal component render and resolve when the
 * user picks something. That plumbing doesn't exist yet, and nothing in
 * this codebase currently calls this function (checked before writing this
 * note). Previously this returned `new Promise((resolve, reject) => {})`
 * with an empty executor — a promise that can never settle, so any caller
 * doing `await useAssetPicker(...)` would hang forever with no error and no
 * indication anything was wrong. Rejecting immediately at least fails
 * loudly instead of silently deadlocking, until the real implementation
 * exists. Use the `<AssetPicker>` component directly (conditionally
 * rendered from component state) instead — see model/nodeTypes/image.tsx
 * for a working example of that pattern.
 *
 * @throws Always rejects — not implemented.
 */
export function useAssetPicker(options?: {
    kind?: 'image' | 'audio' | 'video' | 'doc';
}): Promise<string> {
    return Promise.reject(
        new Error('useAssetPicker() is not implemented — render <AssetPicker> directly instead (see image.tsx).')
    );
}
